/* eslint-disable react/prop-types */
import { tint } from 'polished';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import colors from 'shared-assets/lib/colors';

/**
 * This table is used to show a 2 column table fist column being the table header and second column being values
 * <DetailsTable>
 * <tbody>
 *  <tr>
 *    <th>Header</th>
 *    <td>Value</td>
 *  </tr>
 * </tbody>
 * </DetailsTable>
 */
export const DetailsTable = styled.table.attrs(() => ({
  cellPadding: 0,
  cellSpacing: 0,
  width: '100%',
}))`
  && {
    th,
    td {
      padding-bottom: 0.625rem;
      vertical-align: top;
      padding-top: 0;
      text-align: left;
    }
  }
  th {
    color: ${colors.light};
    font-weight: 400;
    width: 25%;
    padding-right: 1rem;
  }

  th,
  td {
    padding-bottom: 0.5rem;
    padding-top: 0;
    vertical-align: top;
  }

  & tr td:first-child,
  & tr th:first-child {
    padding-left: 0px;
  }

  & tr:last-child td,
  & tr:last-child th {
    padding-bottom: 0px;
  }
`;

const TableWrapper = styled.div`
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  display: block;
  margin-bottom: 1rem;

  table {
    width: 100%;
  }

  thead td {
    border-bottom: 1px solid ${colors.listView.borderColor};
    padding-right: 1rem;
    text-align: left;
  }

  tbody tr td {
    border-bottom: 1px solid ${colors.listView.borderColor};
    padding: 1rem 1rem 1rem 0;
    position: relative;
    text-align: left;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  table tbody tr td:first-child,
  table thead tr td:first-child {
    padding-left: 1rem;
  }

  table tbody tr td:last-child,
  table thead tr td:last-child {
    padding-left: 0;
    padding-right: 1rem;
  }

  table tbody tr td:only-child,
  table thead tr td:only-child {
    padding-left: 1rem;
  }

  tbody.isDoingAFreshLoad {
    display: block;
    min-height: 200px;
  }
`;

const Title = styled.span`
  display: block;
  color: ${tint(0.4, colors.secondary)};
  font-weight: 600;
  margin-top: 0;
  padding: 1rem 0;
  text-transform: uppercase;
`;

export const THead = ({ columns }) => {
  return (
    <thead>
      <tr>
        {Object.entries(columns).map(columnData => {
          let columnKey = columnData[0];
          let columnConfig = columnData[1];
          let title =
            !!columnConfig.title || columnConfig.title === ''
              ? columnConfig.title
              : columnKey;
          let style = !!columnConfig.style ? columnConfig.style : {};

          return (
            <td key={columnKey} style={style}>
              <Title>{title}</Title>
            </td>
          );
        })}
      </tr>
    </thead>
  );
};
THead.propTypes = {
  columns: PropTypes.object.isRequired,
};

// eslint-disable-next-line react/prop-types
export const TBody = ({ rowData, children, emptyRender }) => {
  return (
    <tbody>
      <For each="row" of={rowData || []} index="index">
        <If condition={typeof children === 'function'}>
          {children({ row, index })}
        </If>
      </For>
      <If condition={typeof emptyRender === 'function' && rowData.length === 0}>
        {emptyRender()}
      </If>
    </tbody>
  );
};

TBody.propTypes = {
  children: PropTypes.func.isRequired,
  rowData: PropTypes.array.isRequired,
};

const Table = ({
  columns,
  columnRenderProp,
  children,
  rowData,
  emptyRender,
  tableWrapperStyles,
  ...props
}) => {
  let defaultRowRenderer = ({ row, index }) => {
    return (
      <tr key={!!row && row.id ? row.id : index}>
        {Object.entries(columns).map((column, columnIndex) => {
          let columnKey = column[0];
          let columnData = row[columnKey];
          let columnRenderer =
            !!column[1].render && typeof column[1].render === 'function'
              ? column[1].render
              : columnRenderProp;

          return columnRenderer({
            data: columnData,
            index: columnIndex,
            key: columnKey,
            row,
          });
        })}
      </tr>
    );
  };
  let childRenderProp = !!children ? children : defaultRowRenderer;
  return (
    <TableWrapper style={tableWrapperStyles}>
      <table {...props}>
        <If condition={typeof childRenderProp === 'function'}>
          <THead columns={columns} />
          <TBody rowData={rowData} emptyRender={emptyRender}>
            {childRenderProp}
          </TBody>
        </If>
        {/** this is a built in escape hatch if you want do customize something about the way table renders */}
        <If condition={typeof childRenderProp !== 'function'}>{children}</If>
      </table>
    </TableWrapper>
  );
};

Table.propTypes = {
  children: PropTypes.oneOf([PropTypes.func, PropTypes.node]),
  columns: PropTypes.object.isRequired,
  columnRenderProp: PropTypes.func,
  tableWrapperStyles: PropTypes.object,
};
Table.defaultProps = {
  children: undefined,
  // eslint-disable-next-line react/display-name, react/prop-types
  columnRenderProp: ({ data, index, key }) => {
    return <td key={`${key}-${index}`}>{data}</td>;
  },
  tableWrapperStyles: {},
};

export default Table;
